import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  MORTGAGE_CUSTOMER_SERVICE_DISPLAY,
  MORTGAGE_CUSTOMER_SERVICE_TEL,
} from '@ld.consumer-portal/constants';
import { errorIcon } from '@ld.consumer-portal/data-uri';

import { NumericOnlyDirective, PlatformService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export interface LinkLoanSubmitEvent {
  loanNumber: string;
  zipCode: string;
  ssn: string;
}

@Component({
  selector: 'ld-link-loan-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    NumericOnlyDirective,
    TranslocoDirective,
  ],
  templateUrl: './link-loan-modal.component.html',
  styleUrl: './link-loan-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkLoanModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);

  protected readonly errorIcon = errorIcon;

  protected readonly isNativeMobile = this.platformService.isNativeMobile;

  protected readonly customerServicePhone = MORTGAGE_CUSTOMER_SERVICE_DISPLAY;
  protected readonly customerServiceTel = MORTGAGE_CUSTOMER_SERVICE_TEL;

  readonly userName = input<string>('');

  readonly isLoading = input<boolean>(false);

  readonly errorMessage = input<string | null>(null);

  readonly errorHeader = input<string | null>(null);

  readonly validationAttempts = input<number>(0);

  readonly formSubmit = output<LinkLoanSubmitEvent>();

  readonly closeModal = output<void>();

  readonly formChanged = output<void>();

  protected readonly modalTitle = this.transloco.translate('modalsLinkLoanHelocTitle');

  protected readonly submitButtonText = this.transloco.translate('modalsLinkLoanHelocSubmitButton');

  private readonly destroyRef = inject(DestroyRef);

  protected readonly hideSSN = signal(true);

  protected readonly linkLoanForm = this.fb.group({
    loanNumber: [
      '',
      [Validators.required, Validators.minLength(9), Validators.maxLength(10), Validators.pattern(/^\d+$/)],
    ],
    zipCode: [
      '',
      [Validators.required, Validators.minLength(5), Validators.maxLength(5), Validators.pattern(/^\d{5}$/)],
    ],
    ssn: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4), Validators.pattern(/^\d{4}$/)]],
  });

  protected get isFormValid(): boolean {
    return this.linkLoanForm.valid;
  }

  protected get showCallSupport(): boolean {
    return this.validationAttempts() >= 3;
  }

  ngOnInit(): void {
    this.linkLoanForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.errorMessage()) {
        this.formChanged.emit();
      }
    });

  }

  protected onSubmit(): void {
    if (!this.isFormValid || this.isLoading()) {
      this.linkLoanForm.markAllAsTouched();
      return;
    }

    const formValue = this.linkLoanForm.value;
    this.formSubmit.emit({
      loanNumber: formValue.loanNumber?.trim() || '',
      zipCode: formValue.zipCode?.trim() || '',
      ssn: formValue.ssn?.trim() || '',
    });
  }

  protected onClose(): void {
    this.close();
  }

  protected toggleSSNVisibility(): void {
    this.hideSSN.update((hide) => !hide);
  }

  protected hasError(fieldName: string): boolean {
    const control = this.linkLoanForm.get(fieldName);
    return !!(control?.invalid && control?.touched);
  }

  protected onLoanNumberBlur(): void {
    const control = this.linkLoanForm.get('loanNumber');
    if (control) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }
  }

  protected getFieldError(fieldName: string): string {
    const control = this.linkLoanForm.get(fieldName);
    if (!control?.errors) return '';

    if (control.errors['required']) {
      return this.transloco.translate('commonValidationRequired');
    }
    if (control.errors['minlength'] || control.errors['maxlength'] || control.errors['pattern']) {
      const value = control.value || '';
      const hasNonNumeric = /[^0-9]/.test(value);

      switch (fieldName) {
        case 'loanNumber':
          return this.transloco.translate(hasNonNumeric ? 'validationLoanNumberNumericOnly' : 'validationLoanNumber');
        case 'zipCode':
          return this.transloco.translate(hasNonNumeric ? 'validationZipCodeNumericOnly' : 'validationZipCode');
        case 'ssn':
          return this.transloco.translate(hasNonNumeric ? 'validationNumericOnly' : 'validationSsnLast4');
        default:
          return this.transloco.translate('validationInvalid');
      }
    }
    return '';
  }

  private close(): void {
    this.closeModal.emit();
  }
}
